import { useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from 'recharts';
import type { PricePoint } from '@/hooks/usePrintHistory';

export interface ChartSeries {
  id: string;
  label: string;
  color: string; // hsl(...) or hex
  data: PricePoint[];
}

interface PriceChartProps {
  series: ChartSeries[];
  height?: number;
}

const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'short' });

export function PriceChart({ series, height = 220 }: PriceChartProps) {
  const { merged, min, max } = useMemo(() => {
    const tsSet = new Set<number>();
    series.forEach(s => s.data.forEach(p => tsSet.add(p.t)));
    const ts = [...tsSet].sort((a, b) => a - b);
    let min = Infinity;
    let max = -Infinity;
    const merged = ts.map(t => {
      const row: any = { t };
      for (const s of series) {
        // find nearest <= t
        let v: number | null = null;
        for (const p of s.data) {
          if (p.t <= t) v = p.price;
          else break;
        }
        if (v != null) {
          row[s.id] = v;
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
      return row;
    });
    if (!isFinite(min)) { min = 0; max = 0; }
    return { merged, min, max };
  }, [series]);

  if (!merged.length) {
    return (
      <div className="flex h-[220px] items-center justify-center rounded-xl border border-dashed border-border bg-card/40">
        <p className="text-xs text-muted-foreground font-body">Building price history…</p>
      </div>
    );
  }

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer>
        <AreaChart data={merged} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <defs>
            {series.map(s => (
              <linearGradient key={s.id} id={`g-${s.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.45} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <ReferenceLine y={max} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeOpacity={0.4} />
          <ReferenceLine y={min} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeOpacity={0.4} />
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => MONTH_FMT.format(new Date(v))}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis hide domain={[min * 0.95, max * 1.05]} />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 11,
            }}
            labelFormatter={(v) => new Date(Number(v)).toLocaleDateString()}
            formatter={(v: any) => `$${Number(v).toFixed(2)}`}
          />
          {series.map(s => (
            <Area
              key={s.id}
              type="monotone"
              dataKey={s.id}
              stroke={s.color}
              strokeWidth={2}
              fill={`url(#g-${s.id})`}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[10px] text-muted-foreground font-body tabular-nums">${min.toFixed(2)}</span>
        <span className="text-[10px] text-muted-foreground font-body tabular-nums">${max.toFixed(2)}</span>
      </div>
    </div>
  );
}
